import {
  AppBar,
  AvatarGroup,
  Box,
  Container,
  IconButton,
  Toolbar,
  useTheme as useMuiTheme,
} from "@mui/material";
import { Menu, Settings } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import SettingsModal from "../../components/Settings/SettingsModal";
import { UserAvatar } from "../../components/UserAvatar";
import { useProject } from "../../contexts/ProjectContext";
import { useSidebar } from "../../contexts/SidebarContext";
import { useTheme } from "../../contexts/ThemeContext";
import NotificationsDropdown from "./NotificationsDropdown";
import SearchBar from "./SearchBar";
import UserDropdown from "./UserDropdown";

function Header() {
  const { t } = useTranslation("common");
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const projectContext = useProject();
  const { colors } = useTheme();
  const muiTheme = useMuiTheme();
  const sidebarContext = useSidebar();

  // Safely extract sidebar functionality - will be null if not within SidebarProvider
  const toggleSidebar = sidebarContext?.toggleSidebar || (() => {});
  const isMobile = sidebarContext?.isMobile || false;

  // Safely extract project and role - they may be undefined if used outside ProjectContext
  const project = projectContext?.project;
  const currentUserRole = projectContext?.currentUserRole;

  // Check if current user can see other members
  const canSeeOtherMembers = () => {
    if (currentUserRole === "owner") return true;
    return project?.members_can_see_other_members ?? true;
  };

  // Filter users based on visibility settings
  const getVisibleUsers = () => {
    if (!project?.users) return [];

    if (canSeeOtherMembers()) {
      return project.users;
    } else {
      // Only show current user
      const currentUser = JSON.parse(localStorage.getItem("user"));
      return project.users.filter(
        (user) => user.user_details.id === currentUser?.id,
      );
    }
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundImage: `linear-gradient(90deg, ${muiTheme.palette.primary.main}0F 0%, ${muiTheme.palette.primary.main}05 100%)`,
          boxShadow: "none",
          color: "text.primary",
          backgroundColor: colors.glass?.background || colors.pageSecondary,
          backdropFilter: colors.glass?.blur
            ? `blur(${colors.glass.blur}) saturate(180%)`
            : "none",
          borderBottom: `1px solid ${colors.glass?.border || colors.border.primary}`,
        }}
      >
        <Container
          sx={{
            px: { xs: 2, sm: 4, md: 8 },
            maxWidth: "none !important",
          }}
        >
          <Toolbar
            sx={{
              py: 1,
              height: 66.77,
              display: "flex",
              justifyContent: "space-between",
            }}
            style={{ paddingLeft: 0, paddingRight: 0 }}
          >
            {/* Mobile Menu Button - Only visible on mobile */}
            {isMobile && (
              <IconButton
                onClick={toggleSidebar}
                size="small"
                sx={{
                  color: "text.secondary",
                  mr: 2,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <Menu size={20} strokeWidth={1.5} />
              </IconButton>
            )}

            <Box
              sx={{
                flexGrow: 1,
                maxWidth: 400,
                display: { xs: "none", sm: "block" },
              }}
            >
              <SearchBar placeholder={t("header.searchPlaceholder")} />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              {(() => {
                const visibleUsers = getVisibleUsers();
                return (
                  visibleUsers.length > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", mr: 4 }}>
                      <AvatarGroup
                        max={5}
                        sx={{
                          "& .MuiAvatar-root": {
                            width: 23,
                            height: 23,
                            fontSize: "0.875rem",
                            border: "2px solid",
                            borderColor: "divider",
                          },
                        }}
                      >
                        {visibleUsers.map((user) => (
                          <UserAvatar
                            key={user.id}
                            name={user?.user_details?.name}
                            image={user?.user_details?.avatar}
                            size={23}
                            tooltip={`${user?.user_details?.name || "Unknown"}`}
                            sx={{
                              cursor: "pointer",
                            }}
                          />
                        ))}
                      </AvatarGroup>
                    </Box>
                  )
                );
              })()}
              {/* Right side - Actions */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {/* Settings Icon */}
                <IconButton
                  onClick={() => setShowSettingsModal(true)}
                  size="small"
                  sx={{
                    color: "text.secondary",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <Settings size={20} strokeWidth={1.5} />
                </IconButton>

                <NotificationsDropdown />
                <UserDropdown />
              </Box>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <SettingsModal
        showSettingsModal={showSettingsModal}
        setShowSettingsModal={setShowSettingsModal}
      />
    </>
  );
}

export default Header;
